import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../partials/Sidebar';
import Header from '../../partials/Header';
import vulnApi from '../../services/vulnApi';
import useVulnFilters from '../../hooks/useVulnFilters';
import VulnFilterBar from '../../components/vuln/VulnFilterBar';

const parseJsonField = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try { return JSON.parse(value); } catch { return []; }
};

const COL_COUNT = 9;

function VulnAssets() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [notification, setNotification] = useState(null);
  const { customerId, setCustomerId } = useVulnFilters();

  const [selected, setSelected] = useState(new Set());
  const [showGroupPanel, setShowGroupPanel] = useState(false);
  const [assetGroups, setAssetGroups] = useState([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  // Scan from assets
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanName, setScanName] = useState('');
  const [scanType, setScanType] = useState('quick');
  const [scanSubmitting, setScanSubmitting] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Edit asset
  const [editAsset, setEditAsset] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const fetchAssets = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await vulnApi.getAssets(customerId || undefined);
      setAssets(Array.isArray(data) ? data : []);
      setSelected(new Set());
    } catch (err) {
      setError(err.message || 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    if (!customerId) {
      setAssetGroups([]);
      return;
    }
    try {
      const data = await vulnApi.getAssetGroups(customerId);
      setAssetGroups(Array.isArray(data) ? data : []);
    } catch {
      setAssetGroups([]);
    }
  };

  useEffect(() => { fetchAssets(); }, [customerId]);
  useEffect(() => { fetchGroups(); }, [customerId]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === assets.length) setSelected(new Set());
    else setSelected(new Set(assets.map((a) => a.id)));
  };

  const toggleGroup = (groupId) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
      return next;
    });
  };

  const handleAssignGroup = async (groupId) => {
    if (selected.size === 0) return;
    try {
      await vulnApi.bulkAssignGroup(Array.from(selected), groupId || null);
      setNotification({ type: 'success', text: `${selected.size} asset(s) ${groupId ? 'assigned to group' : 'unassigned'}` });
      fetchAssets();
      fetchGroups();
    } catch (err) {
      setNotification({ type: 'error', text: err.message });
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim() || !customerId) return;
    try {
      await vulnApi.createAssetGroup({
        customer_id: customerId,
        name: newGroupName.trim(),
        description: newGroupDesc.trim() || null,
      });
      setNewGroupName('');
      setNewGroupDesc('');
      setNotification({ type: 'success', text: `Group "${newGroupName.trim()}" created` });
      fetchGroups();
    } catch (err) {
      setNotification({ type: 'error', text: err.message });
    }
  };

  const handleDeleteGroup = async (groupId, groupName) => {
    setDeleteTarget({ type: 'group', id: groupId, label: groupName });
  };

  const handleScanSelected = async () => {
    if (selected.size === 0) return;
    try {
      setScanSubmitting(true);
      const scan = await vulnApi.createScanFromAssets({
        asset_ids: Array.from(selected),
        name: scanName || undefined,
        scan_type: scanType,
      });
      await vulnApi.executeScan(scan.id);
      setShowScanModal(false);
      setScanName('');
      setScanType('quick');
      setNotification({ type: 'success', text: `Scan "${scan.name}" created and started` });
      setTimeout(() => navigate('/vuln/scans'), 1500);
    } catch (err) {
      setNotification({ type: 'error', text: err.message });
    } finally {
      setScanSubmitting(false);
    }
  };

  const handleDeleteAsset = (asset) => {
    setDeleteTarget({ type: 'asset', id: asset.id, label: asset.ip_address });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === 'asset') {
        await vulnApi.deleteAsset(deleteTarget.id);
        setNotification({ type: 'success', text: `Asset ${deleteTarget.label} deleted` });
        fetchAssets();
      } else if (deleteTarget.type === 'group') {
        await vulnApi.deleteAssetGroup(deleteTarget.id);
        setNotification({ type: 'success', text: `Group "${deleteTarget.label}" deleted` });
        fetchGroups();
        fetchAssets();
      }
    } catch (err) {
      setNotification({ type: 'error', text: err.message });
    } finally {
      setDeleteTarget(null);
    }
  };

  const openEditModal = (asset) => {
    setEditAsset(asset);
    setEditForm({
      hostname: asset.hostname || '',
      os_fingerprint: asset.os_fingerprint || '',
      asset_type: asset.asset_type || 'host',
      network_type: asset.network_type || 'external',
    });
  };

  const handleEditSave = async () => {
    if (!editAsset) return;
    try {
      setEditSaving(true);
      const updates = {};
      if (editForm.hostname !== (editAsset.hostname || '')) updates.hostname = editForm.hostname || null;
      if (editForm.os_fingerprint !== (editAsset.os_fingerprint || '')) updates.os_fingerprint = editForm.os_fingerprint || null;
      if (editForm.asset_type !== editAsset.asset_type) updates.asset_type = editForm.asset_type;
      if (editForm.network_type !== editAsset.network_type) updates.network_type = editForm.network_type;
      if (Object.keys(updates).length > 0) {
        await vulnApi.updateAsset(editAsset.id, updates);
        setNotification({ type: 'success', text: `Asset ${editAsset.ip_address} updated` });
        fetchAssets();
      }
      setEditAsset(null);
    } catch (err) {
      setNotification({ type: 'error', text: err.message });
    } finally {
      setEditSaving(false);
    }
  };

  const toggleExpanded = (id) => setExpandedId(expandedId === id ? null : id);

  const osIcon = (fingerprint) => {
    if (!fingerprint) return <span className="text-slate-400">—</span>;
    const fp = fingerprint.toLowerCase();
    const iconClass = 'w-5 h-5 inline-block';
    let icon = null;
    if (fp.includes('windows')) {
      icon = (
        <svg className={iconClass} viewBox="0 0 24 24" fill="currentColor" style={{ color: '#0078D4' }}>
          <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801" />
        </svg>
      );
    } else if (fp.includes('linux') || fp.includes('ubuntu') || fp.includes('debian') || fp.includes('centos') || fp.includes('redhat') || fp.includes('red hat') || fp.includes('fedora') || fp.includes('suse') || fp.includes('arch') || fp.includes('gentoo') || fp.includes('kali')) {
      icon = (
        <svg className={iconClass} viewBox="0 0 24 24" fill="currentColor" style={{ color: '#FCC624' }}>
          <path d="M12.504 0c-.155 0-.315.008-.48.021-4.226.333-3.105 4.807-3.17 6.298-.076 1.092-.3 1.953-1.05 3.02-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489a.424.424 0 00-.11.135c-.26.268-.45.6-.663.839-.199.199-.485.267-.797.4-.313.136-.658.269-.864.68-.09.189-.136.394-.132.602 0 .199.027.4.055.536.058.399.116.728.04.97-.249.68-.28 1.145-.106 1.484.174.334.535.47.94.601.81.2 1.91.135 2.774.6.926.466 1.866.67 2.616.47.526-.116.97-.464 1.208-.946.587-.003 1.23-.269 2.26-.334.699-.058 1.574.267 2.577.2.025.134.063.198.114.333l.003.003c.391.778 1.113 1.345 1.884 1.345.358 0 .705-.167 1.025-.4.263-.2.39-.476.456-.79.193-.4.118-.834.003-1.187a.463.463 0 00.321-.267c.117-.27.139-.572.139-.839 0-.466-.154-.862-.374-1.133a2.673 2.673 0 00-.016-1.334c.021-.065.035-.134.064-.198.24-.532.214-1.065.089-1.534a3.025 3.025 0 00-.559-1.066c-.167-.267-.377-.504-.577-.715l-.004-.007c-.08-.093-.147-.18-.2-.265.334-1.135.247-2.201-.066-3.131l-.004-.013c-.32-.998-.882-1.865-1.58-2.498C13.92 1.087 13.198.404 12.504 0zm2.095 8.143c.504 0 .862.467.862 1.069 0 .597-.358 1.066-.862 1.066-.504 0-.862-.469-.862-1.066 0-.602.358-1.069.862-1.069zm-3.715.063c.5 0 .858.47.858 1.068 0 .602-.358 1.069-.858 1.069s-.863-.467-.863-1.069.363-1.068.863-1.068z" />
        </svg>
      );
    } else if (fp.includes('mac') || fp.includes('darwin') || fp.includes('ios') || fp.includes('iphone') || fp.includes('ipad') || fp.includes('apple')) {
      icon = (
        <svg className={iconClass} viewBox="0 0 24 24" fill="currentColor" style={{ color: '#A2AAAD' }}>
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
        </svg>
      );
    } else if (fp.includes('android')) {
      icon = (
        <svg className={iconClass} viewBox="0 0 24 24" fill="currentColor" style={{ color: '#3DDC84' }}>
          <path d="M17.523 15.3414c-.5-1.468-1.8-2.58-3.4-2.876-.3-.058-.612-.086-.912-.086s-.612.028-.912.086c-1.6.296-2.9 1.408-3.4 2.876L7 17h10l-1.477-1.659zM12.001 2.532c-2.568 0-4.8 1.5-5.852 3.656l1.204 1.752c.66-1.756 2.38-3.008 4.384-3.008h.528c2.004 0 3.724 1.252 4.384 3.008l1.204-1.752C16.8 4.032 14.568 2.532 12 2.532zm5.888 5.268H6.112A6.002 6.002 0 006 9v.6c0 .06.012.12.012.18.12 1.632.96 3.06 2.244 3.936.036.024.084.036.12.06.516.336 1.092.588 1.716.732.3.072.6.12.912.132h1.992c.312-.012.612-.06.912-.132.624-.144 1.2-.396 1.716-.732.036-.024.084-.036.12-.06 1.284-.876 2.124-2.304 2.244-3.936.001-.06.012-.12.012-.18V9a6.002 6.002 0 00-.112-.6z" />
          <path d="M8.4 3.8l-1.2-1.8M15.6 3.8l1.2-1.8" strokeWidth="0.8" stroke="#3DDC84" fill="none" strokeLinecap="round" />
        </svg>
      );
    } else if (fp.includes('freebsd') || fp.includes('openbsd') || fp.includes('netbsd')) {
      icon = (
        <svg className={iconClass} viewBox="0 0 24 24" fill="currentColor" style={{ color: '#AB2B28' }}>
          <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 18c-4.418 0-8-3.582-8-8s3.582-8 8-8 8 3.582 8 8-3.582 8-8 8zm-2-9.5a1.5 1.5 0 11-3.001-.001A1.5 1.5 0 0110 10.5zm7 0a1.5 1.5 0 11-3.001-.001A1.5 1.5 0 0117 10.5z" />
        </svg>
      );
    } else if (fp.includes('cisco') || fp.includes('juniper') || fp.includes('fortinet') || fp.includes('palo alto')) {
      icon = (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#049FD9' }}>
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <path d="M6 12h.01M10 12h.01M14 12h.01M18 12h.01" strokeWidth="3" />
        </svg>
      );
    } else {
      icon = (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: '#94a3b8' }}>
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <path d="M8 21h8M12 17v4" />
        </svg>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5" title={fingerprint}>
        {icon}
        <span className="max-w-[160px] truncate text-xs text-slate-500 dark:text-slate-400">{fingerprint}</span>
      </span>
    );
  };

  const typeBadge = (type) => {
    const styles = {
      host: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300',
      server: 'bg-indigo-100 dark:bg-indigo-500/30 text-indigo-600 dark:text-indigo-400',
      workstation: 'bg-blue-100 dark:bg-blue-500/30 text-blue-600 dark:text-blue-400',
      laptop: 'bg-sky-100 dark:bg-sky-500/30 text-sky-600 dark:text-sky-400',
      mobile: 'bg-cyan-100 dark:bg-cyan-500/30 text-cyan-600 dark:text-cyan-400',
      network_device: 'bg-violet-100 dark:bg-violet-500/30 text-violet-600 dark:text-violet-400',
      router: 'bg-purple-100 dark:bg-purple-500/30 text-purple-600 dark:text-purple-400',
      switch: 'bg-fuchsia-100 dark:bg-fuchsia-500/30 text-fuchsia-600 dark:text-fuchsia-400',
      firewall: 'bg-red-100 dark:bg-red-500/30 text-red-600 dark:text-red-400',
      load_balancer: 'bg-orange-100 dark:bg-orange-500/30 text-orange-600 dark:text-orange-400',
      iot: 'bg-teal-100 dark:bg-teal-500/30 text-teal-600 dark:text-teal-400',
      cloud_instance: 'bg-amber-100 dark:bg-amber-500/30 text-amber-600 dark:text-amber-400',
      container: 'bg-emerald-100 dark:bg-emerald-500/30 text-emerald-600 dark:text-emerald-400',
      virtual_machine: 'bg-lime-100 dark:bg-lime-500/30 text-lime-600 dark:text-lime-400',
      database: 'bg-yellow-100 dark:bg-yellow-500/30 text-yellow-600 dark:text-yellow-400',
      storage: 'bg-stone-100 dark:bg-stone-500/30 text-stone-600 dark:text-stone-400',
      printer: 'bg-zinc-100 dark:bg-zinc-500/30 text-zinc-600 dark:text-zinc-400',
      wap: 'bg-rose-100 dark:bg-rose-500/30 text-rose-600 dark:text-rose-400',
      voip: 'bg-pink-100 dark:bg-pink-500/30 text-pink-600 dark:text-pink-400',
      scada: 'bg-red-200 dark:bg-red-600/30 text-red-700 dark:text-red-300',
      other: 'bg-gray-100 dark:bg-gray-500/30 text-gray-600 dark:text-gray-400',
    };
    return styles[type] || styles.host;
  };

  // Group assets: grouped first, then ungrouped (only when groups are loaded)
  const grouped = {};
  const ungrouped = [];
  assets.forEach((a) => {
    if (a.asset_group_id && assetGroups.length > 0) {
      if (!grouped[a.asset_group_id]) grouped[a.asset_group_id] = [];
      grouped[a.asset_group_id].push(a);
    } else {
      ungrouped.push(a);
    }
  });

  const renderAssetRow = (asset) => {
    const services = parseJsonField(asset.services);
    const isExpanded = expandedId === asset.id;

    return (
      <React.Fragment key={asset.id}>
        <tr className="hover:bg-slate-50 dark:hover:bg-slate-700/30">
          <td className="px-3 py-3 whitespace-nowrap w-px">
            <input
              type="checkbox"
              checked={selected.has(asset.id)}
              onChange={() => toggleSelect(asset.id)}
              className="rounded border-slate-300 dark:border-slate-600"
            />
          </td>
          <td className="px-5 py-3 whitespace-nowrap cursor-pointer" onClick={() => toggleExpanded(asset.id)}>
            <span className="font-mono font-bold text-slate-800 dark:text-slate-100">{asset.ip_address}</span>
          </td>
          <td className="px-5 py-3 whitespace-nowrap text-slate-600 dark:text-slate-300">{asset.hostname || '—'}</td>
          <td className="px-5 py-3 whitespace-nowrap text-slate-600 dark:text-slate-300">
            {osIcon(asset.os_fingerprint)}
          </td>
          <td className="px-5 py-3 whitespace-nowrap">
            <span className={`inline-flex font-medium rounded-full text-center px-2.5 py-0.5 text-xs ${typeBadge(asset.asset_type)}`}>
              {(asset.asset_type || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/\bIot\b/, 'IoT').replace(/\bWap\b/, 'WAP').replace(/\bVoip\b/, 'VoIP').replace(/\bScada\b/, 'SCADA').replace(/\bIcs\b/, 'ICS')}
            </span>
          </td>
          <td className="px-5 py-3 whitespace-nowrap">
            <button
              className="inline-flex items-center text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium"
              onClick={() => toggleExpanded(asset.id)}
            >
              {services.length}
              <svg className={`w-4 h-4 ml-1 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </td>
          <td className="px-5 py-3 whitespace-nowrap text-slate-600 dark:text-slate-300">
            {asset.last_seen ? new Date(asset.last_seen).toLocaleDateString() : '—'}
          </td>
          <td className="px-5 py-3 whitespace-nowrap text-right">
            <div className="flex items-center justify-end space-x-1">
              <button onClick={() => openEditModal(asset)} className="text-slate-400 hover:text-indigo-500" title="Edit asset">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button onClick={() => handleDeleteAsset(asset)} className="text-slate-400 hover:text-rose-500" title="Delete asset">
                <svg className="w-5 h-5 fill-current" viewBox="0 -3 32 32">
                  <path d="M13 15h2v6h-2zM17 15h2v6h-2z" />
                  <path d="M20 9c0-.6-.4-1-1-1h-6c-.6 0-1 .4-1 1v2H8v2h1v10c0 .6.4 1 1 1h12c.6 0 1-.4 1-1V13h1v-2h-4V9zm-6 1h4v1h-4v-1zm7 3v9H11v-9h10z" />
                </svg>
              </button>
            </div>
          </td>
        </tr>

        {isExpanded && services.length > 0 && (
          <tr>
            <td colSpan={COL_COUNT} className="px-5 py-3 bg-slate-50 dark:bg-slate-900/50">
              <div className="text-sm">
                <h4 className="font-medium text-slate-700 dark:text-slate-300 mb-2">Services</h4>
                <table className="w-full">
                  <thead className="text-xs text-slate-500 dark:text-slate-400">
                    <tr>
                      <th className="text-left py-1">Port</th>
                      <th className="text-left py-1">Protocol</th>
                      <th className="text-left py-1">Service</th>
                      <th className="text-left py-1">Product</th>
                      <th className="text-left py-1">Version</th>
                      <th className="text-left py-1">CPE</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-600 dark:text-slate-300">
                    {services.map((svc, i) => (
                      <tr key={i}>
                        <td className="py-1 font-mono">{svc.port}</td>
                        <td className="py-1">{svc.protocol}</td>
                        <td className="py-1">{svc.service || '—'}</td>
                        <td className="py-1">{svc.product || '—'}</td>
                        <td className="py-1">{svc.version || '—'}</td>
                        <td className="py-1 font-mono text-xs">{(svc.cpe || []).join(', ') || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </td>
          </tr>
        )}

        {isExpanded && services.length === 0 && (
          <tr>
            <td colSpan={COL_COUNT} className="px-5 py-3 bg-slate-50 dark:bg-slate-900/50 text-sm text-slate-500 dark:text-slate-400">
              No services detected for this asset.
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        <main className="grow">
          <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">

            <div className="sm:flex sm:justify-between sm:items-center mb-8">
              <div className="mb-4 sm:mb-0">
                <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
                  Asset Inventory{' '}
                  {!loading && <span className="text-slate-400 dark:text-slate-500 font-medium text-xl">({assets.length})</span>}
                </h1>
              </div>
              <VulnFilterBar customerId={customerId} onCustomerChange={setCustomerId}>
                {selected.size > 0 && (
                  <button
                    onClick={() => setShowScanModal(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded text-sm font-medium bg-indigo-500 hover:bg-indigo-600 text-white transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    Scan Selected ({selected.size})
                  </button>
                )}
                {customerId && (
                  <button
                    onClick={() => setShowGroupPanel(!showGroupPanel)}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded text-sm font-medium transition-colors ${
                      showGroupPanel
                        ? 'bg-indigo-500 text-white'
                        : 'border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                    </svg>
                    Manage Groups
                  </button>
                )}
              </VulnFilterBar>
            </div>

            {notification && (
              <div className={`px-4 py-3 rounded-sm mb-6 ${notification.type === 'success' ? 'bg-emerald-100 dark:bg-emerald-400/20 text-emerald-600 dark:text-emerald-400' : 'bg-rose-100 dark:bg-rose-400/20 text-rose-600 dark:text-rose-400'}`}>
                <div className="flex justify-between items-center">
                  <span>{notification.text}</span>
                  <button onClick={() => setNotification(null)} className="ml-4 text-current opacity-70 hover:opacity-100">
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
                  </button>
                </div>
              </div>
            )}

            {/* Group management panel */}
            {showGroupPanel && customerId && (
              <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 p-5 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-slate-800 dark:text-slate-100">Asset Groups</h2>
                  <button onClick={() => setShowGroupPanel(false)} className="text-slate-400 hover:text-slate-500">
                    <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
                  </button>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-2">Existing Groups</h3>
                    {assetGroups.length === 0 ? (
                      <p className="text-sm text-slate-400">No groups yet for this customer.</p>
                    ) : (
                      <div className="space-y-2">
                        {assetGroups.map((g) => (
                          <div key={g.id} className="flex items-center justify-between bg-slate-50 dark:bg-slate-700/30 rounded px-3 py-2">
                            <div>
                              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{g.name}</span>
                              {g.description && <span className="text-xs text-slate-400 ml-2">{g.description}</span>}
                            </div>
                            <div className="flex items-center gap-2">
                              {selected.size > 0 && (
                                <button onClick={() => handleAssignGroup(g.id)} className="text-xs text-indigo-500 hover:text-indigo-600 font-medium">
                                  Assign ({selected.size})
                                </button>
                              )}
                              <button onClick={() => handleDeleteGroup(g.id, g.name)} className="text-slate-400 hover:text-rose-500" title="Delete group">
                                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
                              </button>
                            </div>
                          </div>
                        ))}
                        {selected.size > 0 && (
                          <button onClick={() => handleAssignGroup(null)} className="text-xs text-slate-500 hover:text-slate-600 font-medium">
                            Unassign selected ({selected.size}) from any group
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-2">Create Group</h3>
                    <div className="space-y-2">
                      <input type="text" value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} placeholder="Group name (e.g. PCI CDE)" className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm" />
                      <input type="text" value={newGroupDesc} onChange={(e) => setNewGroupDesc(e.target.value)} placeholder="Description (optional)" className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm" />
                      <button onClick={handleCreateGroup} disabled={!newGroupName.trim()} className="btn bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded text-sm disabled:opacity-50">Create Group</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-rose-100 dark:bg-rose-500/20 text-rose-600 dark:text-rose-400 px-4 py-3 rounded-sm mb-6">{error}</div>
            )}

            {loading && (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" />
              </div>
            )}

            {!loading && !error && (
              <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700">
                <div className="overflow-x-auto">
                  <table className="table-auto w-full">
                    <thead className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-b border-slate-200 dark:border-slate-700">
                      <tr>
                        <th className="px-3 py-3 whitespace-nowrap w-px">
                          <input type="checkbox" checked={assets.length > 0 && selected.size === assets.length} onChange={toggleAll} className="rounded border-slate-300 dark:border-slate-600" />
                        </th>
                        <th className="px-5 py-3 whitespace-nowrap text-left">IP Address</th>
                        <th className="px-5 py-3 whitespace-nowrap text-left">Hostname</th>
                        <th className="px-5 py-3 whitespace-nowrap text-left">OS</th>
                        <th className="px-5 py-3 whitespace-nowrap text-left">Type</th>
                        <th className="px-5 py-3 whitespace-nowrap text-left">Services</th>
                        <th className="px-5 py-3 whitespace-nowrap text-left">Last Seen</th>
                        <th className="px-5 py-3 whitespace-nowrap text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
                      {assets.length === 0 ? (
                        <tr>
                          <td colSpan={COL_COUNT} className="px-5 py-8 text-center text-slate-500 dark:text-slate-400">
                            No assets discovered yet. Run a scan to populate the asset inventory.
                          </td>
                        </tr>
                      ) : (
                        <>
                          {/* Grouped assets */}
                          {assetGroups.map((group) => {
                            const groupAssets = grouped[group.id];
                            if (!groupAssets || groupAssets.length === 0) return null;
                            const isCollapsed = collapsedGroups.has(group.id);
                            const groupSelected = groupAssets.filter((a) => selected.has(a.id)).length;

                            return (
                              <React.Fragment key={`group-${group.id}`}>
                                <tr
                                  className="bg-slate-100 dark:bg-slate-700/50 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700"
                                  onClick={() => toggleGroup(group.id)}
                                >
                                  <td className="px-3 py-2 w-px">
                                    <svg className={`w-4 h-4 text-slate-500 transform transition-transform ${isCollapsed ? '' : 'rotate-90'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  </td>
                                  <td colSpan={COL_COUNT - 1} className="px-5 py-2">
                                    <div className="flex items-center gap-3">
                                      <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                                      </svg>
                                      <span className="font-semibold text-slate-800 dark:text-slate-100">{group.name}</span>
                                      <span className="text-xs text-slate-400">({groupAssets.length} asset{groupAssets.length !== 1 ? 's' : ''})</span>
                                      {group.description && <span className="text-xs text-slate-400 italic">{group.description}</span>}
                                      {groupSelected > 0 && <span className="text-xs text-indigo-500 font-medium">{groupSelected} selected</span>}
                                    </div>
                                  </td>
                                </tr>
                                {!isCollapsed && groupAssets.map(renderAssetRow)}
                              </React.Fragment>
                            );
                          })}

                          {/* Ungrouped assets */}
                          {ungrouped.length > 0 && Object.keys(grouped).length > 0 && (
                            <tr className="bg-slate-50 dark:bg-slate-700/30">
                              <td colSpan={COL_COUNT} className="px-5 py-2">
                                <span className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase">Ungrouped ({ungrouped.length})</span>
                              </td>
                            </tr>
                          )}
                          {ungrouped.map(renderAssetRow)}
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          </div>
        </main>
      </div>

      {/* Scan from selected assets modal */}
      {showScanModal && (
        <div className="fixed inset-0 bg-slate-900 bg-opacity-70 z-50 flex items-center justify-center px-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-sm">
            <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Scan {selected.size} Asset{selected.size !== 1 ? 's' : ''}</h2>
                <button onClick={() => setShowScanModal(false)} className="text-slate-400 hover:text-slate-500">
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
                </button>
              </div>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Scan Name</label>
                <input type="text" value={scanName} onChange={(e) => setScanName(e.target.value)} placeholder="e.g. Re-scan DMZ hosts" className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Scan Type</label>
                <select value={scanType} onChange={(e) => setScanType(e.target.value)} className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm">
                  <option value="quick">Quick</option>
                  <option value="standard">Standard</option>
                  <option value="full">Full</option>
                  <option value="udp">UDP</option>
                </select>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end space-x-3">
              <button onClick={() => setShowScanModal(false)} className="btn border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 px-4 py-2 rounded text-sm">Cancel</button>
              <button onClick={handleScanSelected} disabled={scanSubmitting} className="btn bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded text-sm disabled:opacity-50">{scanSubmitting ? 'Starting...' : 'Start Scan'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit asset modal */}
      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setDeleteTarget(null)}
          ></div>
          <div className="relative max-w-md w-full bg-white dark:bg-slate-800 rounded-lg shadow-lg p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <svg className="w-10 h-10 fill-current text-amber-500" viewBox="0 0 24 24">
                  <path d="M12 2L1 21h22L12 2zm0 3.99L19.53 19H4.47L12 5.99zM11 16h2v2h-2v-2zm0-6h2v4h-2v-4z"/>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">
                  Confirm Deletion
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {deleteTarget.type === 'asset' ? (
                    <>Are you sure you want to delete asset <span className="font-medium text-slate-800 dark:text-slate-200">{deleteTarget.label}</span>? All findings for this asset will also be removed. This action cannot be undone.</>
                  ) : (
                    <>Are you sure you want to delete group <span className="font-medium text-slate-800 dark:text-slate-200">"{deleteTarget.label}"</span>? Assets will be unassigned but not deleted.</>
                  )}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm font-medium text-white bg-rose-600 rounded-md hover:bg-rose-700 transition-colors"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {editAsset && (
        <div className="fixed inset-0 bg-slate-900 bg-opacity-70 z-50 flex items-center justify-center px-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-sm">
            <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Edit {editAsset.ip_address}</h2>
                <button onClick={() => setEditAsset(null)} className="text-slate-400 hover:text-slate-500">
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
                </button>
              </div>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Hostname</label>
                <input type="text" value={editForm.hostname} onChange={(e) => setEditForm({ ...editForm, hostname: e.target.value })} className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">OS Fingerprint</label>
                <input type="text" value={editForm.os_fingerprint} onChange={(e) => setEditForm({ ...editForm, os_fingerprint: e.target.value })} className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Asset Type</label>
                <select value={editForm.asset_type} onChange={(e) => setEditForm({ ...editForm, asset_type: e.target.value })} className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm">
                  <option value="host">Host</option>
                  <option value="server">Server</option>
                  <option value="workstation">Workstation</option>
                  <option value="laptop">Laptop</option>
                  <option value="mobile">Mobile Device</option>
                  <option value="network_device">Network Device</option>
                  <option value="router">Router</option>
                  <option value="switch">Switch</option>
                  <option value="firewall">Firewall</option>
                  <option value="load_balancer">Load Balancer</option>
                  <option value="wap">Wireless AP</option>
                  <option value="iot">IoT Device</option>
                  <option value="cloud_instance">Cloud Instance</option>
                  <option value="container">Container</option>
                  <option value="virtual_machine">Virtual Machine</option>
                  <option value="database">Database</option>
                  <option value="storage">Storage</option>
                  <option value="printer">Printer</option>
                  <option value="voip">VoIP Phone</option>
                  <option value="scada">SCADA/ICS</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Network Type</label>
                <select value={editForm.network_type} onChange={(e) => setEditForm({ ...editForm, network_type: e.target.value })} className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm">
                  <option value="external">External</option>
                  <option value="internal">Internal</option>
                </select>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end space-x-3">
              <button onClick={() => setEditAsset(null)} className="btn border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 px-4 py-2 rounded text-sm">Cancel</button>
              <button onClick={handleEditSave} disabled={editSaving} className="btn bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded text-sm disabled:opacity-50">{editSaving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VulnAssets;
